import { useState, FormEvent } from "react";
import PhoneNumberInput from "../../components/Inputs/PhoneNumberInput";
import axios from "../../services/axios/backendAxios";
import AsyncSelect from "react-select/async";
import { Company } from "../../types";
import SubtleButton from "../../components/Buttons/SubtleButton";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { invalidateQueriesContaining } from "../../utils/queryInvalidation";
import flash from "../../services/flash";
type FormPayload = {
  company_id: string;
  phone: string;
};
export default function AddUserToCompany() {
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (data: FormPayload) => {
      return axios.post(`company/developer/add-user`, data);
    },
    onSuccess: (resp) => {
      flash.success(resp?.data?.message ?? "User added");
    },
    onSettled: () => {
      invalidateQueriesContaining(queryClient, [
        "company",
        "companies",
        "user",
      ]);
    },
    onError: (err) => {
      flash.error("Error saving: " + (err.message ?? err));
    },
  });
  return (
    <div>
      <h2 className="text-lg font-bold">Add To Company</h2>
      <p>
        WARNING: may cause issues if the user is already a member of a company.
        Only works for users who have already signed in at least once.
      </p>
      <form
        onSubmit={(e: FormEvent<HTMLFormElement>) => {
          e.preventDefault();
          const form = e.currentTarget as HTMLFormElement;
          const formData = new FormData(form);
          const data = Object.fromEntries(formData.entries()) as FormPayload;
          save.mutate(data);
        }}
      >
        <AdminCompanySelect name="company_id" />
        {/* @ts-expect-error not going to include all the undefined props for this js component */}
        <PhoneNumberInput required={true} name="phone" />
        <div>
          <input type="checkbox" name="make_admin" /> Make Admin
        </div>
        <SubtleButton type="submit" className="mt-2">
          Add User to Company
        </SubtleButton>
      </form>
    </div>
  );
}

export function AdminCompanySelect({
  name,
  onChange,
}: {
  name: string;
  onChange?: (company: Company | null) => void;
}) {
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const loadOptions = async (inputValue: string) => {
    if (!inputValue || inputValue.length < 3) return [];
    const res = await axios.get(
      `/company/list?q=${encodeURIComponent(inputValue)}`,
    );
    const data = await res.data;
    const companies = data.companies as Company[];
    return companies.map((c) => ({ label: c.name, value: c._id, raw: c }));
  };
  return (
    <AsyncSelect
      name={name}
      cacheOptions
      defaultOptions={false}
      loadOptions={loadOptions}
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onChange={(option: any) => {
        setSelectedCompany(option ? option.raw : null);
        if (typeof onChange === "function") {
          onChange(option ? option.raw : null);
        }
      }}
      placeholder="Search companies (min 3 chars)..."
      // Render the menu into a portal so it isn't clipped by parent overflow
      menuPortalTarget={
        typeof document !== "undefined" ? document.body : undefined
      }
      menuPosition="fixed"
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          borderColor: "#829399",
        }),
        // ensure the portal'd menu is drawn above other content
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      }}
      value={
        selectedCompany
          ? { label: selectedCompany.name, value: selectedCompany._id }
          : null
      }
      isClearable
    />
  );
}
